import { ComponentProps } from 'lib/component-props';
import { LinkField, Page, useSitecore } from '@sitecore-content-sdk/nextjs';
import { JSX, useEffect, useState } from 'react';
import PlainTextWrapper from 'helpers/SitecoreWrappers/PlainTextWrapper/PlainTextWrapper';
import Link from 'next/link';
import { SvgIcon } from 'helpers/SvgIcon';
import ComponentWrapper from 'helpers/SitecoreWrappers/ComponentWrapper/ComponentWrapper';
import { Search } from '.generated/Feature.CW.model';
import { Pages } from '.generated/Project.CW.model';
import { Result } from '@coveo/headless';
import ImageWrapper from 'helpers/SitecoreWrappers/ImageWrapper/ImageWrapper';
import RichTextWrapper from 'helpers/SitecoreWrappers/RichTextWrapper/RichTextWrapper';
import { CoveoFacetFields, PageTemplateIds } from 'lib/constants';
import { useSiteSettings } from 'lib/utils/page-data-static-props/pageDataContext';
import { guidsAreEqual } from 'lib/utils/string-utils';

export type RelatedBlogsProps = ComponentProps &
  Search.RelatedBlogs & {
    contentType: string;
    isSidenavPart?: boolean;
    variant?: 'condensed' | 'expanded';
    params: {
      Theme: string;
    };
  };

/*
 * Related blogs component: Show related blogs based on descending order
 * */
const RelatedBlogs = (props: RelatedBlogsProps): JSX.Element => {
  const { page } = useSitecore();
  const blogContentType = getBlogContentType(props, page) || '';

  return (
    <ComponentWrapper className="py-[80px] bg-theme-bg">
      <div
        className={`max-w-screen-xxl mx-auto  ${props?.isSidenavPart ? 'lg:px-0' : 'lg:px-[80px] px-10'}`}
      >
        <PlainTextWrapper
          field={props?.fields?.title}
          tag="h2"
          className="text-36 leading-[43px] font-bold font-centuryGothic text-gray-900 mb-[56px]"
        />
        <RelatedBlogsResultList {...props} contentType={blogContentType} />
      </div>
    </ComponentWrapper>
  );
};

function getBlogContentType(props: ComponentProps, sitecoreContext: Page): string | undefined {
  const renderingType = props?.rendering?.params?.blogContentType;
  if (!!renderingType) return renderingType;
  const route = sitecoreContext?.layout?.sitecore?.route;
  if (!guidsAreEqual(route?.templateId, PageTemplateIds.blogDetailPage)) {
    return undefined;
  }
  const pageInfo = route as Pages.BlogDetailPage;
  return pageInfo?.fields?.blogContentType?.value;
}

// Unified component that handles both condensed and expanded variants
export const RelatedBlogsUnified = (props: RelatedBlogsProps): JSX.Element => {
  const { page } = useSitecore();
  const blogContentType = getBlogContentType(props, page) || '';

  return (
    <ComponentWrapper className="py-[80px] bg-theme-bg minal" params={props.params}>
      <div
        className={`max-w-screen-xxl mx-auto ${props?.isSidenavPart ? 'lg:px-0' : 'lg:px-[80px] px-10'}`}
      >
        <h2 className="!text-36 !leading-[43px] font-bold font-centuryGothic text-theme-text mb-[56px]">
          <PlainTextWrapper field={props?.fields?.title} />
        </h2>
        <RelatedBlogsResultList {...props} contentType={blogContentType} />
      </div>
    </ComponentWrapper>
  );
};

// Convenience components for backward compatibility
export const CondensedVersion = (props: RelatedBlogsProps): JSX.Element => {
  return <RelatedBlogsUnified {...props} variant="condensed" />;
};

export const ExpandedVersion = (props: RelatedBlogsProps): JSX.Element => {
  return <RelatedBlogsUnified {...props} variant="expanded" />;
};

const RelatedBlogsResultList = ({
  contentType,
  variant = 'expanded',
  ...props
}: RelatedBlogsProps): JSX.Element => {
  const [results, setResults] = useState<Result[]>([]);
  const [totalResults, setTotalResults] = useState<number>(0);
  const blogDisplayCount = props?.rendering?.params?.blogDisplayCount;
  const relatedArticleDisplayCount = (blogDisplayCount && parseInt(blogDisplayCount)) || 3;

  const { page } = useSitecore();
  const contextWithSettings = useSiteSettings()?.fields;
  const blogSearchPage = contextWithSettings?.blogSearchPage as LinkField;

  useEffect(() => {
    const itemId = page.layout?.sitecore?.route?.itemId;
    if (!contentType || !itemId) return;

    const performSearch = async (itemId: string) => {
      try {
        const res = await fetch(`/api/coveo-proxy/related-blogs/${contentType}`);
        if (!res.ok) return;
        const { totalCount, results } = (await res.json()) as {
          totalCount: number;
          results: (Result & { raw: { cwitemid?: string } })[];
        };

        const filteredResults = results
          .filter((i) => i.raw.cwitemid !== itemId.replace(/-/g, ''))
          .slice(0, relatedArticleDisplayCount);
        setResults(filteredResults);

        // There should always be n-1 in this topic after filtering the current page out
        setTotalResults(totalCount - 1);
      } catch (error) {
        console.error('Error fetching related blogs:', error);
        return undefined;
      }
    };

    performSearch(itemId);
  }, [contentType, relatedArticleDisplayCount, page.layout?.sitecore?.route?.itemId]);

  if (results.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">No related articles found.</p>
      </div>
    );
  }

  return (
    <>
      <div
        className={`grid grid-cols-1  gap-[32px] ${!props?.isSidenavPart ? 'lg:grid-cols-3' : 'lg:grid-cols-2'}`}
      >
        {results.map((item: Result & { raw: { cwitemid?: string } }) => {
          return (
            <article
              key={item.raw.cwitemid}
              role="region"
              aria-labelledby={`${item?.raw?.cwtitle}`}
              className="border-b border-b-gray-50 last:border-0 lg:gap-[46px] gap-[24px] justify-between group shadow-default hover:shadow-hover rounded-md bg-white"
            >
              <div>
                <Link
                  aria-label={`${item?.raw?.cwtitle} blog title`}
                  href={item?.clickUri}
                  prefetch={false}
                  // onClick={() => item?.clickUri}
                  // onMouseDown={() => controller?.select()}
                >
                  {' '}
                  <ImageWrapper
                    field={{
                      value: {
                        src: item?.raw?.cwheroimage as string,
                        alt: item?.raw?.cwtitle as string,
                      },
                    }}
                    className="w-full rounded-t-md"
                  />
                </Link>

                <div className="p-12">
                  <div className="flex w-full justify-between ">
                    <Link
                      aria-label={`${item?.raw?.cwblogcontenttype} blog content type`}
                      href={`${blogSearchPage?.value?.href}?${CoveoFacetFields.blogContentType}=${item?.raw?.cwblogcontenttype}`}
                    >
                      <PlainTextWrapper
                        field={{ value: item?.raw?.cwblogcontenttype as string }}
                        tag="p"
                        className="text-blue-base text-14 leading-22 font-bold font-centuryGothic pb-2 hover:text-darkblue-base"
                      />
                    </Link>
                    {variant === 'expanded' && (
                      <PlainTextWrapper
                        tag="p"
                        // field={{ value: formatDate(item?.raw?.cwblogdate as number) }}
                        // aria-label={`Published on ${formatDate(item?.raw?.cwblogdate as number)}`}
                        className="text-14 leading-22 font-nunitoSans text-gray-900"
                      />
                    )}
                  </div>
                  <div className="flex justify-between">
                    <Link
                      aria-label={`${item?.raw?.cwtitle} blog title`}
                      className="text-blue-base text-28 leading-31 font-bold font-centuryGothic block py-6 group-hover:underline group-hover:text-darkblue-base cursor-pointer flex-1"
                      href={item?.clickUri}
                      // onClick={() => controller?.select()}
                      // onMouseDown={() => controller?.select()}
                    >
                      <PlainTextWrapper field={{ value: item?.raw?.cwtitle as string }} tag="h3" />
                    </Link>
                    <SvgIcon
                      icon="arrow-right"
                      size="lg"
                      className="text-blue-base opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                    />
                  </div>

                  {item?.raw?.cwdescription !== undefined && (
                    <div className="text-gray-900 font-nunitoSans text-16 leading-25 font-[500] overflow-hidden line-clamp-2 max-w-full">
                      <RichTextWrapper
                        field={{
                          value:
                            (item?.raw?.cwdescription as string)?.substring(0, 200) +
                            ((item?.raw?.cwdescription as string)?.length > 200 ? '...' : ''),
                        }}
                        className="overflow-hidden"
                      />
                    </div>
                  )}

                  {variant === 'expanded' && (
                    <div className="flex gap-[8px] pt-6 pb-2 leading-22 text-14 text-[#111] font-nunitoSans">
                      <Link
                        href={`${blogSearchPage?.value?.href}?${CoveoFacetFields.blogAuthorName}=${item?.raw?.cwauthorname}`}
                        aria-label={`See more blog posts by ${item?.raw?.cwauthorname}`}
                        className="font-bold !text-blue-base hover:underline"
                      >
                        <PlainTextWrapper field={{ value: item?.raw?.cwauthorname as string }} />
                      </Link>

                      {item?.raw?.cwauthortitle !== undefined &&
                        item?.raw?.cwauthorname !== undefined && <span>|</span>}
                      <PlainTextWrapper field={{ value: item?.raw?.cwauthortitle as string }} />
                    </div>
                  )}
                </div>
              </div>
            </article>
          );
        })}
      </div>

      {/* Show "View More Stories" only if there are more results than displayed */}
      {totalResults > relatedArticleDisplayCount && blogSearchPage && (
        <div className="mt-[32px] flex">
          <Link
            href={`${blogSearchPage?.value?.href}?${CoveoFacetFields.blogContentType}=${contentType}`}
            className="w-fit flex-row theme-text-btn border-theme-text-btn-border lg:flex font-bold font-centuryGothic gap-x-4 justify-center rounded-sm lg:text-center text-left !text-theme-text-btn-text active:bg-theme-text-btn-hover active:text-theme-text-btn-hover-text active:border-theme-text-btn-hover-border  focus:outline-offset-2 focus:outline-2 focus:outline-theme-text-btn-outline focus:border-theme-text-btn-border focus:outline hover:text-theme-text-btn-hover-text hover:underline hover:border-theme-text-btn-hover-border flex items-center"
          >
            <PlainTextWrapper field={props?.fields?.viewMoreCTA} tag="span" />
            <SvgIcon icon="arrow-right" size="md" className="text-theme-text-btn-text" />
          </Link>
        </div>
      )}
    </>
  );
};

export default RelatedBlogs;
